"use client";

import { ProspectActionKind } from "@prisma/client";
import ActionMenu from "@/app/(components)/generics/ActionMenu/ActionMenu";
import IconArrowDown from "@/app/(components)/generics/Icons/IconArrowDown";
import IconClock from "@/app/(components)/generics/Icons/IconClock";
import IconPlus from "@/app/(components)/generics/Icons/IconPlus";
import { TKey } from "@/app/translations/TKey";
import { useT } from "@/app/translations/useT";
import { ProspectActionTypeItem } from "@/app/types/prospect/ProspectAction";
import { ProspectTableRow } from "@/app/types/prospect/ProspectTableRow";
import { UDate } from "@/app/utils/UDate";
import {
  lastProspectAction,
  prospectActionKindOptions,
  prospectActionLabel,
} from "./ProspectActions.logic";
import styles from "./ProspectActions.module.css";

type Props = {
  prospect: ProspectTableRow;
  lang: string;
  actionTypes: ProspectActionTypeItem[];
  onSelect?: (kind: ProspectActionKind, type?: ProspectActionTypeItem) => void;
  onCreateType?: () => void;
  onOpenHistory?: () => void;
};

export default function ProspectActionSelect(props: Props) {
  const T = useT();
  const { prospect, lang, actionTypes, onSelect, onCreateType, onOpenHistory } = props;

  const actions = prospect.actions ?? [];
  const last = lastProspectAction(actions);
  const doneKinds = new Set(actions.map((action) => action.kind));
  const doneTypeIds = new Set(
    actions.map((action) => action.type?.id).filter((id): id is number => !!id),
  );

  return (
    <div className={styles.select}>
      <ActionMenu
        scrollable
        searchPlaceholder={T(TKey.PROSPECT_ACTION_SEARCH_PLACEHOLDER) as string}
        searchEmptyLabel={T(TKey.PROSPECT_ACTION_SEARCH_EMPTY) as string}
        icon={
          <>
            <span className={styles.selectLabel}>{last ? prospectActionLabel(last, T) : ""}</span>
            <IconArrowDown size={16} />
          </>
        }
        items={[
          ...(onSelect
            ? prospectActionKindOptions(T).map((option) => ({
                label: option.label,
                checked: doneKinds.has(option.kind),
                onClick: () => onSelect(option.kind),
              }))
            : []),
          ...(onSelect
            ? actionTypes.map((type) => ({
                label: type.label,
                checked: doneTypeIds.has(type.id),
                onClick: () => onSelect(ProspectActionKind.CUSTOM, type),
              }))
            : []),
        ]}
        searchAction={
          onCreateType && {
            label: String(T(TKey.PROSPECT_ACTION_CREATE_TYPE)),
            icon: <IconPlus size={14} />,
            onClick: onCreateType,
          }
        }
        footerItems={[
          onOpenHistory && {
            label: String(T(TKey.PROSPECT_ACTION_OPEN_HISTORY)),
            icon: <IconClock size={14} />,
            onClick: onOpenHistory,
          },
        ]}
      />

      {last && (
        <span className={styles.selectAgo}>{UDate.timeAgo(new Date(last.doneAt), lang)}</span>
      )}
    </div>
  );
}
